import React, { useState } from "react";
import { useBilling } from "../store/BillingContext";
import { Card } from "../components/common/Card";
import { money, formatDate } from "../lib/utils";

type AccountingTab = "pnl" | "balance_sheet" | "aging" | "tax_ledger";
type PeriodFilter = "all" | "fy26" | "q3" | "month";

export const AccountingView: React.FC = () => {
  const { state, setView } = useBilling();
  const [activeTab, setActiveTab] = useState<AccountingTab>("pnl");
  const [period, setPeriod] = useState<PeriodFilter>("all");

  const invoices = state.invoices;
  const expenses = state.expenses;

  // 1. Calculations
  const paidInvoices = invoices.filter((i) => i.status === "paid");
  const outstandingInvoices = invoices.filter((i) => !["paid", "cancelled"].includes(i.status));

  // Gross Revenue Realized (from paid invoices)
  const realizedRevenue = paidInvoices.reduce((sum, i) => sum + (Number(i.total) || 0), 0);
  const realizedSubtotal = paidInvoices.reduce((sum, i) => sum + (Number(i.subtotal - i.discTotal) || 0), 0);
  const outputGstCollected = paidInvoices.reduce((sum, i) => sum + (Number(i.gstAmt) || 0), 0);

  // Total Accounts Receivable (AR)
  const accountsReceivable = outstandingInvoices.reduce((sum, i) => sum + (Number(i.total) || 0), 0);
  const totalInvoicedAccrual = invoices
    .filter((i) => i.status !== "cancelled")
    .reduce((sum, i) => sum + (Number(i.total) || 0), 0);

  // Total Operating Expenses Outflow
  const totalExpenses = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

  // Estimated Input Tax Credit (ITC) on expenses (standard 18% GST estimate)
  const inputTaxCredit = Math.round(totalExpenses * (18 / 118));
  const expenseExcludingGst = totalExpenses - inputTaxCredit;

  // Net Profit Realized
  const netProfit = realizedRevenue - totalExpenses;
  const netProfitMargin = realizedRevenue > 0 ? (netProfit / realizedRevenue) * 100 : 0;

  // Accrual Net Profit
  const accrualNetProfit = totalInvoicedAccrual - totalExpenses;

  // Net GST Liability to Govt = Output GST Collected - Input Tax Credit
  const netGstPayable = Math.max(0, outputGstCollected - inputTaxCredit);

  // Expenses Breakdown by Category
  const expenseByCategory: Record<string, number> = {};
  expenses.forEach((e) => {
    const cat = e.category || "General";
    expenseByCategory[cat] = (expenseByCategory[cat] || 0) + Number(e.amount || 0);
  });

  // Recurring Subscriptions & Monthly SaaS Burn
  const recurringExpenses = expenses.filter(
    (e) => e.recurrence && e.recurrence !== "one_time" && e.status !== "cancelled"
  );
  const monthlyRecurringBurn = recurringExpenses.reduce((sum, e) => {
    const amt = Number(e.amount) || 0;
    if (e.recurrence === "monthly") return sum + amt;
    if (e.recurrence === "annual") return sum + Math.round(amt / 12);
    if (e.recurrence === "quarterly") return sum + Math.round(amt / 3);
    return sum;
  }, 0);

  // Export P&L to CSV Handler
  const handleExportCsv = () => {
    const rows = [
      ["Revival Softech - Financial P&L Statement"],
      ["Generated At", new Date().toLocaleString()],
      [""],
      ["REVENUE & COLLECTIONS", "AMOUNT (INR)"],
      ["Gross Realized Revenue (Settled)", realizedRevenue.toString()],
      ["Accounts Receivable (Pending Invoices)", accountsReceivable.toString()],
      ["Total Accrual Invoiced Revenue", totalInvoicedAccrual.toString()],
      [""],
      ["OPERATING EXPENSES", "AMOUNT (INR)"],
      ...Object.entries(expenseByCategory).map(([cat, amt]) => [cat, amt.toString()]),
      ["Total Operating Expenses", totalExpenses.toString()],
      [""],
      ["NET FINANCIAL RESULTS", "AMOUNT (INR)"],
      ["Net Cash Profit", netProfit.toString()],
      ["Net Profit Margin", `${netProfitMargin.toFixed(1)}%`],
      ["Output GST Collected", outputGstCollected.toString()],
      ["Input Tax Credit (ITC)", inputTaxCredit.toString()],
      ["Net GST Payable to Govt", netGstPayable.toString()],
    ];

    const csvContent =
      "data:text/csv;charset=utf-8," +
      rows.map((e) => e.map((x) => `"${x.replace(/"/g, '""')}"`).join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Revival_Softech_Accounting_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {/* Top Header & Period Control */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "18px",
        }}
      >
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {[
            { id: "all", label: "All Financial Records" },
            { id: "fy26", label: "FY 2026-27" },
            { id: "q3", label: "Q3 (Jul - Sep)" },
            { id: "month", label: "Current Month" },
          ].map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id as PeriodFilter)}
              style={{
                padding: "6px 14px",
                fontSize: "12px",
                borderRadius: "var(--radius-full)",
                border: "1px solid",
                borderColor: period === p.id ? "var(--brand-navy)" : "var(--border-subtle)",
                background: period === p.id ? "var(--brand-navy)" : "#ffffff",
                color: period === p.id ? "#ffffff" : "var(--text-main)",
                fontWeight: period === p.id ? 600 : 500,
                cursor: "pointer",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => window.print()}
            style={{
              padding: "7px 12px",
              fontSize: "12px",
              background: "#ffffff",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-sm)",
              fontWeight: 600,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <i className="fa-solid fa-print" /> Print Statement
          </button>
          <button
            className="gold"
            onClick={handleExportCsv}
            style={{
              padding: "7px 12px",
              fontSize: "12px",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <i className="fa-solid fa-file-csv" /> Export CSV
          </button>
        </div>
      </div>

      {/* Top Financial Health KPIs */}
      <section className="cards">
        <Card
          title="Net Cash Profit"
          value={money(netProfit)}
          icon="fa-scale-balanced"
          subtitle={`${netProfitMargin >= 0 ? "+" : ""}${netProfitMargin.toFixed(1)}% Realized Margin`}
        />
        <Card
          title="Realized Revenue"
          value={money(realizedRevenue)}
          icon="fa-indian-rupee-sign"
          subtitle={`${paidInvoices.length} invoices paid in full`}
        />
        <Card
          title="Operating Expenses"
          value={money(totalExpenses)}
          icon="fa-wallet"
          subtitle={`${expenses.length} recorded cash outflows`}
        />
        <Card
          title="Accounts Receivable"
          value={money(accountsReceivable)}
          icon="fa-hourglass-half"
          subtitle={`${outstandingInvoices.length} pending client payments`}
        />
      </section>

      {/* Navigation Sub-Tabs */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid var(--border-subtle)",
          marginBottom: "20px",
          gap: "8px",
          overflowX: "auto",
        }}
      >
        {[
          { id: "pnl", label: "Profit & Loss (P&L)", icon: "fa-chart-line" },
          { id: "balance_sheet", label: "Balance Sheet", icon: "fa-building-columns" },
          { id: "aging", label: "Receivables Aging", icon: "fa-clock" },
          { id: "tax_ledger", label: "GST & Tax Ledger", icon: "fa-receipt" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id as AccountingTab)}
            style={{
              padding: "10px 16px",
              fontSize: "13.5px",
              fontWeight: activeTab === t.id ? 700 : 500,
              color: activeTab === t.id ? "var(--brand-navy)" : "var(--text-muted)",
              border: "none",
              borderBottom: activeTab === t.id ? "3px solid var(--brand-gold)" : "3px solid transparent",
              background: "transparent",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              borderRadius: 0,
              whiteSpace: "nowrap",
            }}
          >
            <i className={`fa-solid ${t.icon}`} />
            {t.label}
          </button>
        ))}
      </div>

      {/* TAB 1: PROFIT & LOSS (P&L) STATEMENT */}
      {activeTab === "pnl" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "20px" }}>
          {/* Income Side */}
          <div className="panel" style={{ margin: 0 }}>
            <div className="panel-title">
              <h3>
                <i className="fa-solid fa-arrow-trend-up" style={{ color: "#10b981", marginRight: "8px" }} />
                Operating Revenue (Income)
              </h3>
              <strong style={{ color: "#10b981", fontSize: "16px" }}>{money(realizedRevenue)}</strong>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {paidInvoices.map((inv) => (
                <div
                  key={inv.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 10px",
                    background: "#f8fafc",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "13px",
                  }}
                >
                  <div>
                    <b>{inv.company}</b>
                    <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                      {inv.number} • Paid {formatDate(inv.date)}
                    </div>
                  </div>
                  <strong style={{ color: "var(--brand-navy)" }}>{money(inv.total)}</strong>
                </div>
              ))}

              <div
                style={{
                  marginTop: "12px",
                  paddingTop: "12px",
                  borderTop: "1px dashed var(--border-subtle)",
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "13px",
                }}
              >
                <span>Accrual Revenue (Unpaid Invoices):</span>
                <strong style={{ color: "#d97706" }}>+{money(accountsReceivable)}</strong>
              </div>

              <div
                style={{
                  padding: "10px",
                  background: "#f0fdf4",
                  borderRadius: "var(--radius-sm)",
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#166534",
                }}
              >
                <span>Total Invoiced Operations:</span>
                <span>{money(totalInvoicedAccrual)}</span>
              </div>
            </div>
          </div>

          {/* Expense Side */}
          <div className="panel" style={{ margin: 0 }}>
            <div className="panel-title">
              <h3>
                <i className="fa-solid fa-arrow-trend-down" style={{ color: "#ef4444", marginRight: "8px" }} />
                Operating Expenses (Outflow)
              </h3>
              <strong style={{ color: "#ef4444", fontSize: "16px" }}>{money(totalExpenses)}</strong>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {Object.entries(expenseByCategory).map(([cat, amt]) => {
                const pct = totalExpenses > 0 ? (amt / totalExpenses) * 100 : 0;
                return (
                  <div key={cat} style={{ padding: "6px 8px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
                      <span>{cat}</span>
                      <strong>{money(amt)} ({pct.toFixed(0)}%)</strong>
                    </div>
                    <div style={{ width: "100%", height: "6px", background: "#f1f5f9", borderRadius: "3px", overflow: "hidden" }}>
                      <div
                        style={{
                          width: `${pct}%`,
                          height: "100%",
                          background: "var(--brand-navy)",
                          borderRadius: "3px",
                        }}
                      />
                    </div>
                  </div>
                );
              })}

              {recurringExpenses.length > 0 && (
                <div
                  style={{
                    marginTop: "10px",
                    padding: "10px 12px",
                    background: "#f8fafc",
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid var(--border-subtle)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12.5px" }}>
                    <span style={{ fontWeight: 600, color: "var(--brand-navy)" }}>
                      <i className="fa-solid fa-arrows-rotate" style={{ color: "var(--brand-gold)", marginRight: "6px" }} />
                      Fixed Tool & SaaS Subscriptions (Claude Code, Cloud)
                    </span>
                    <strong style={{ color: "#0369a1" }}>{money(monthlyRecurringBurn)}/mo</strong>
                  </div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "4px" }}>
                    {recurringExpenses.map((r) => `${r.description || r.vendor} (${money(r.amount)})`).join(" • ")}
                  </div>
                </div>
              )}

              <div
                style={{
                  marginTop: "14px",
                  padding: "12px",
                  background: netProfit >= 0 ? "#f0fdf4" : "#fef2f2",
                  border: `1px solid ${netProfit >= 0 ? "#bbf7d0" : "#fecaca"}`,
                  borderRadius: "var(--radius-sm)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontWeight: 700, fontSize: "14px", color: netProfit >= 0 ? "#166534" : "#991b1b" }}>
                    NET OPERATING PROFIT
                  </span>
                  <strong style={{ fontSize: "18px", color: netProfit >= 0 ? "#166534" : "#991b1b" }}>
                    {money(netProfit)}
                  </strong>
                </div>
                <div style={{ fontSize: "11.5px", color: "var(--text-muted)", marginTop: "4px" }}>
                  Net Margin: <b>{netProfitMargin.toFixed(1)}%</b> • EBITDA Realized
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: BALANCE SHEET SNAPSHOT */}
      {activeTab === "balance_sheet" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "20px" }}>
          {/* Assets */}
          <div className="panel" style={{ margin: 0 }}>
            <div className="panel-title">
              <h3>Current Assets</h3>
              <strong style={{ color: "var(--brand-navy)" }}>{money(realizedRevenue + accountsReceivable)}</strong>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "13.5px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", background: "#f8fafc", borderRadius: "var(--radius-sm)" }}>
                <span>Liquid Cash & Bank Collections:</span>
                <b>{money(realizedRevenue - totalExpenses)}</b>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", background: "#f8fafc", borderRadius: "var(--radius-sm)" }}>
                <span>Accounts Receivable (Customer Dues):</span>
                <b style={{ color: "#d97706" }}>{money(accountsReceivable)}</b>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", background: "#f8fafc", borderRadius: "var(--radius-sm)" }}>
                <span>Input Tax Credit (ITC Asset):</span>
                <b>{money(inputTaxCredit)}</b>
              </div>

              <div
                style={{
                  marginTop: "16px",
                  padding: "12px",
                  background: "#f1f5f9",
                  borderRadius: "var(--radius-sm)",
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: 800,
                  fontSize: "15px",
                }}
              >
                <span>TOTAL ASSETS</span>
                <span>{money(realizedRevenue + accountsReceivable)}</span>
              </div>
            </div>
          </div>

          {/* Liabilities & Equity */}
          <div className="panel" style={{ margin: 0 }}>
            <div className="panel-title">
              <h3>Liabilities & Working Capital</h3>
              <strong style={{ color: "var(--brand-navy)" }}>{money(realizedRevenue + accountsReceivable)}</strong>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "13.5px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", background: "#f8fafc", borderRadius: "var(--radius-sm)" }}>
                <span>Net GST Tax Liability Payable:</span>
                <b style={{ color: "#ef4444" }}>{money(netGstPayable)}</b>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", background: "#f8fafc", borderRadius: "var(--radius-sm)" }}>
                <span>Operating Accounts Payable:</span>
                <b>₹0.00 (Current)</b>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", background: "#f8fafc", borderRadius: "var(--radius-sm)" }}>
                <span>Retained Earnings / Equity:</span>
                <b style={{ color: "#166534" }}>{money(realizedRevenue + accountsReceivable - netGstPayable)}</b>
              </div>

              <div
                style={{
                  marginTop: "16px",
                  padding: "12px",
                  background: "#f1f5f9",
                  borderRadius: "var(--radius-sm)",
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: 800,
                  fontSize: "15px",
                }}
              >
                <span>TOTAL LIABILITIES & EQUITY</span>
                <span>{money(realizedRevenue + accountsReceivable)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: RECEIVABLES AGING SCHEDULE */}
      {activeTab === "aging" && (
        <div className="panel" style={{ margin: 0 }}>
          <div className="panel-title">
            <div>
              <h3>Accounts Receivable Aging Schedule</h3>
              <p style={{ margin: "4px 0 0", fontSize: "12.5px", color: "var(--text-muted)" }}>
                Track unsettled client balances by payment due window
              </p>
            </div>
            <strong style={{ color: "#d97706", fontSize: "16px" }}>
              Total Outstanding: {money(accountsReceivable)}
            </strong>
          </div>

          <div className="desktop-table-view table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Client / Entity</th>
                  <th>Issue Date</th>
                  <th>Due Date</th>
                  <th>Aging Status</th>
                  <th style={{ textAlign: "right" }}>Pending Balance</th>
                  <th style={{ textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {outstandingInvoices.length > 0 ? (
                  outstandingInvoices.map((inv) => (
                    <tr key={inv.id}>
                      <td>
                        <span className="mobile-doc-num">{inv.number}</span>
                      </td>
                      <td>
                        <b>{inv.company}</b>
                        <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>{inv.email}</div>
                      </td>
                      <td>{formatDate(inv.date)}</td>
                      <td>{formatDate(inv.due)}</td>
                      <td>
                        <span
                          style={{
                            fontSize: "11px",
                            padding: "3px 8px",
                            borderRadius: "10px",
                            fontWeight: 700,
                            background: inv.status === "overdue" ? "#fef2f2" : "#fef3c7",
                            color: inv.status === "overdue" ? "#991b1b" : "#b45309",
                          }}
                        >
                          {inv.status === "overdue" ? "Overdue (60+ days)" : "Current (0-30 days)"}
                        </span>
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 700, color: "#0f172a" }}>
                        {money(inv.total)}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          onClick={() => setView("invoices")}
                          style={{ padding: "4px 8px", fontSize: "11.5px" }}
                        >
                          View Invoice →
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="empty">
                      All invoices are settled! Zero outstanding accounts receivable.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile view for aging */}
          <div className="mobile-cards-view">
            {outstandingInvoices.map((inv) => (
              <div key={inv.id} className="mobile-doc-card">
                <div className="mobile-doc-top">
                  <span className="mobile-doc-num">{inv.number}</span>
                  <span style={{ fontSize: "11px", color: "#b45309", fontWeight: 700 }}>
                    Due: {formatDate(inv.due)}
                  </span>
                </div>
                <div className="mobile-doc-company">{inv.company}</div>
                <div className="mobile-doc-email">{inv.email}</div>
                <div className="mobile-doc-amount">
                  <small>Pending Due</small>
                  <strong style={{ color: "#d97706" }}>{money(inv.total)}</strong>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 4: GST & TAX AUDIT LEDGER */}
      {activeTab === "tax_ledger" && (
        <div className="panel" style={{ margin: 0 }}>
          <div className="panel-title">
            <div>
              <h3>Goods & Services Tax (GST) Summary Ledger</h3>
              <p style={{ margin: "4px 0 0", fontSize: "12.5px", color: "var(--text-muted)" }}>
                Agency GSTIN: <b>{state.settings.gstin || "29AAAAA0000A1Z5"}</b> • Karnataka (State 29)
              </p>
            </div>
            <strong style={{ color: "var(--brand-navy)", fontSize: "16px" }}>
              Net Tax Payable: {money(netGstPayable)}
            </strong>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "16px", marginBottom: "20px" }}>
            <div style={{ background: "#f8fafc", padding: "14px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)" }}>
              <small style={{ textTransform: "uppercase", fontSize: "11px", color: "var(--text-muted)", fontWeight: 700 }}>
                Output GST Collected (Sales)
              </small>
              <div style={{ fontSize: "20px", fontWeight: 800, color: "var(--brand-navy)", marginTop: "4px" }}>
                {money(outputGstCollected)}
              </div>
              <small style={{ fontSize: "11px", color: "var(--text-muted)" }}>From paid tax invoices</small>
            </div>

            <div style={{ background: "#f8fafc", padding: "14px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)" }}>
              <small style={{ textTransform: "uppercase", fontSize: "11px", color: "var(--text-muted)", fontWeight: 700 }}>
                Input Tax Credit (ITC on Purchases)
              </small>
              <div style={{ fontSize: "20px", fontWeight: 800, color: "#10b981", marginTop: "4px" }}>
                {money(inputTaxCredit)}
              </div>
              <small style={{ fontSize: "11px", color: "var(--text-muted)" }}>Eligible business deduction</small>
            </div>

            <div style={{ background: "#f8fafc", padding: "14px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)" }}>
              <small style={{ textTransform: "uppercase", fontSize: "11px", color: "var(--text-muted)", fontWeight: 700 }}>
                Net Payable to Tax Authority
              </small>
              <div style={{ fontSize: "20px", fontWeight: 800, color: "#ef4444", marginTop: "4px" }}>
                {money(netGstPayable)}
              </div>
              <small style={{ fontSize: "11px", color: "var(--text-muted)" }}>After claiming valid ITC</small>
            </div>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Tax Component</th>
                  <th>Rate</th>
                  <th>Applicability</th>
                  <th style={{ textAlign: "right" }}>Calculated Tax</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><b>CGST (Central GST)</b></td>
                  <td>9%</td>
                  <td>Intra-state supply within Karnataka</td>
                  <td style={{ textAlign: "right", fontWeight: 600 }}>{money(outputGstCollected / 2)}</td>
                </tr>
                <tr>
                  <td><b>SGST (State GST)</b></td>
                  <td>9%</td>
                  <td>Intra-state supply within Karnataka</td>
                  <td style={{ textAlign: "right", fontWeight: 600 }}>{money(outputGstCollected / 2)}</td>
                </tr>
                <tr>
                  <td><b>Less: Eligible Input Tax Credit (ITC)</b></td>
                  <td>18%</td>
                  <td>Software, Cloud & Hardware expenses</td>
                  <td style={{ textAlign: "right", fontWeight: 600, color: "#10b981" }}>-{money(inputTaxCredit)}</td>
                </tr>
                <tr style={{ background: "#f8fafc", fontWeight: 800 }}>
                  <td colSpan={3}>NET GOVERNMENT GST REMITTANCE</td>
                  <td style={{ textAlign: "right", color: "var(--brand-navy)", fontSize: "14.5px" }}>{money(netGstPayable)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};
